import Modal from "@/components/common/modal";
import { DialogFooter } from "@/components/ui/dialog";
import { useDuplicateFlow, useGetFlow } from "@/queries/flows/flows.api";
import {
  Avatar,
  AvatarFallback,
  Button,
  Icon,
  TextAreaField,
  useToast,
} from "@elaniintc/goes-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { z } from "zod";

// Schema de validación para el formulario
const newFlowVersionSchema = z.object({
  // name: z
  //   .string()
  //   .min(1, "El nombre es requerido")
  //   .max(100, "El nombre no puede exceder 100 caracteres"),
  version_description: z
    .string()
    .min(5, "El comentario es requerido")
    .max(255, "El comentario no puede exceder 250 caracteres"),
});

type newFlowVersionFormData = z.infer<typeof newFlowVersionSchema>;

interface Props {
  flowId: string | null;
  latestVersion: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (newVersion: number) => void;
}

const NewFlowVersionModal = ({
  flowId,
  latestVersion,
  isOpen,
  onClose,
  onSuccess,
}: Props) => {
  const client = useQueryClient();
  const { data: flowData, isLoading: isLoadingFlowData } = useGetFlow(
    flowId!,
    false,
  );
  const { mutateAsync, isPending: isLoading } = useDuplicateFlow(flowId!);
  const { showToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<newFlowVersionFormData>({
    resolver: zodResolver(newFlowVersionSchema),
    defaultValues: {
      //name: "",
      version_description: "",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const [version_description] = watch(["version_description"]);

  const onSubmit = async (formData: newFlowVersionFormData) => {
    const newVersion = latestVersion + 1;

    console.log("flowData", flowData?.data);
    console.log("formData", formData);

    try {
      await mutateAsync({
        name: flowData?.data.name || "",
        //name: formData.name,
        version_description: formData.version_description,
        versioning: true,
      });
      showToast({
        title: "Duplicación en proceso",
        description:
          "El flujo se está duplicando. Podrás ver sus detalles y editarlo una vez de haya duplicado.",
        options: {
          type: "success",
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="info" shape="square">
                <Icon type="fas" name="copy" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
      onSuccess?.(newVersion);
      onClose();
    } catch (error) {
      const e = error as AxiosError<{ message: string }>;
      setError("version_description", {
        type: "server",
        message:
          e?.response?.data?.message ||
          e?.message ||
          "Error al crear la nueva versión",
      });
    }
  };

  const handleClose = () => {
    reset({
      //name: "",
      version_description: "",
    });
    clearErrors();
    client.invalidateQueries({
      queryKey: ["admin-flow-detail", flowId],
      exact: true,
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen && !!flowData}
      onClose={handleClose}
      title="Crear nueva versión"
      description="¿Estas seguro de que quieres crear una nueva versión? Al hacerlo crearás una nueva versión en construcción."
      modalContentClassName="max-w-[520px] w-full gap-7"
    >
      <form
        className="flex flex-col gap-6"
        onSubmit={handleSubmit(onSubmit)}
        id="duplicate-flow-form"
      >
        {/* <TextField
          label="Nombre de la versión"
          required
          placeholder="Escribe un nombre para la versión"
          error={errors.name?.message}
          helperText="Este nombre será asignado al flujo principal de esta versión."
          {...register("name")}
        /> */}
        <TextAreaField
          label="Motivo de esta nueva versión"
          required
          maxLength={120}
          placeholder="Descripción del motivo para esta nueva versión"
          rows={3}
          className="resize-none"
          error={errors.version_description?.message}
          {...register("version_description")}
          count={version_description?.length || 0}
          limit={120}
        />

        <DialogFooter className="pt-1">
          <div className="w-full grid grid-cols-1 tablet:grid-cols-2 gap-3">
            <Button
              size="lg"
              type="button"
              className="w-full"
              variant="tertiary"
              onClick={handleClose}
              disabled={isLoading || isLoadingFlowData}
            >
              Cancelar
            </Button>
            <Button
              size="lg"
              className="w-full"
              type="submit"
              disabled={isLoading || !isValid || isLoadingFlowData}
              isLoading={isLoading}
            >
              Crear versión
            </Button>
          </div>
        </DialogFooter>
      </form>
    </Modal>
  );
};

export default NewFlowVersionModal;
